import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessRequestItem, RequestStatusType } from '../../../hooks/useUserPermissions';
import { Icons, Typography } from '../../../stories/atoms';
import { ColumnFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { HeaderRowItem, Table } from '../../../stories/molecules/tablev2/Table';
import { commonTranslations } from '../../../translations';
import { accessRequestTranslations } from '../../../translations/accessRequest';
import { convertCamelCaseToTitleCase, formatDateWithTimestamp } from '../../../utils';
import { StatusTableitem } from '../statusTableItem/StatusTableItem';
import { getDaysLeftForAccessExpiry } from '../util';
import { StyledIconWithText } from './AccessApprovalTable.styles';

enum RequestColumnType {
  Email = 'email',
  Status = 'status',
  Role = 'role',
  StatusTimestamp = 'statusTimestamp',
  Action = 'action',
}

export type AccessApprovalTableProps = {
  requestItems: AccessRequestItem[];
  sendTableAction: (action: RequestStatusType, requestId: string) => void;
};

const {
  triggerNotificationModal: triggerNotificationModalTranslations,
  manageAccessModal: manageAccessModalTranslations,
} = accessRequestTranslations;

// Helper function to determine action label and icon
const getActionDetails = (status: RequestStatusType, lastUpdated: string) => {
  const isExpired = getDaysLeftForAccessExpiry(lastUpdated, 365) <= 0;

  if (status === 'Pending') {
    return {
      label: manageAccessModalTranslations.reviewRequest,
      icon: Icons.SEARCH,
      action: 'Pending' as RequestStatusType,
      isDisabled: false,
      testId: 'pending-action',
    };
  }
  if (isExpired) {
    return {
      label: triggerNotificationModalTranslations.sendEmail,
      icon: Icons.EMAIL,
      action: 'Expired' as RequestStatusType,
      isDisabled: true,
      testId: 'expired-action',
    };
  }
  return {
    label: manageAccessModalTranslations.modifyRequest,
    icon: Icons.EDIT,
    action: status === 'Approved' ? 'Approved' : ('Rejected' as RequestStatusType),
    isDisabled: true,
    testId: status === 'Approved' ? 'approved-action' : 'rejected-action',
  };
};

export const AccessApprovalTable: FC<AccessApprovalTableProps> = ({ requestItems, sendTableAction }) => {
  const { t } = useTranslation();

  const headerItems: HeaderRowItem<RequestColumnType>[] = [
    {
      id: RequestColumnType.Email,
      content: (
        <Typography>
          {t(accessRequestTranslations.email.key, accessRequestTranslations.email.defaultValue)}
        </Typography>
      ),
    },
    {
      id: RequestColumnType.Status,
      content: (
        <Typography>
          {t(accessRequestTranslations.status.key, accessRequestTranslations.status.defaultValue)}
        </Typography>
      ),
    },
    {
      id: RequestColumnType.Role,
      content: (
        <Typography>
          {t(
            accessRequestTranslations.manageAccessModal.role.key,
            accessRequestTranslations.manageAccessModal.role.defaultValue,
          )}
        </Typography>
      ),
    },
    {
      id: RequestColumnType.StatusTimestamp,
      content: (
        <Typography>
          {t(
            accessRequestTranslations.statusTimestamp.key,
            accessRequestTranslations.statusTimestamp.defaultValue,
          )}
        </Typography>
      ),
    },
    {
      id: RequestColumnType.Action,
      content: <Typography>{t(commonTranslations.action.key, commonTranslations.action.defaultValue)}</Typography>,
    },
  ];

  const handleActionClick = (action: RequestStatusType, requestId: string) => {
    sendTableAction(action, requestId);
  };

  return (
    <ColumnFlexContainer flex={1} padding={[4, 0]} minHeight={[0]} data-testid="access-request-table">
      <Table
        items={requestItems}
        headerItems={headerItems}
        stickyHeader
        stickyLastColumn
        disableHover
        getRowItems={(requestItem: AccessRequestItem) => {
          const {
            label: actionLabel,
            icon: actionicon,
            action,
            isDisabled,
            testId,
          } = getActionDetails(requestItem.status, requestItem.statusTimestamp);
          return [
            {
              id: RequestColumnType.Email,
              width: '20%',
              content: <Typography>{requestItem.upn}</Typography>,
            },
            {
              id: RequestColumnType.Status,
              content: <StatusTableitem accessRequestItem={requestItem} />,
            },

            {
              id: RequestColumnType.Role,
              content: (
                <Typography>
                  {requestItem.requestedAssignment.title
                    ? convertCamelCaseToTitleCase(requestItem.requestedAssignment.title)
                    : '-'}
                </Typography>
              ),
            },
            {
              id: RequestColumnType.StatusTimestamp,
              content: (
                <Typography>
                  {requestItem.statusTimestamp ? formatDateWithTimestamp(requestItem.statusTimestamp) : '-'}
                </Typography>
              ),
            },
            {
              id: RequestColumnType.Action,
              width: '15%',
              content: (
                <StyledIconWithText
                  data-testid={testId}
                  label={t(actionLabel.key, actionLabel.defaultValue)}
                  isDisabled={isDisabled} // TODO: Remove this when the expired action is implemented [BTCOBUI-3288]
                  textOptions={{
                    type: 'body3',
                  }}
                  iconOptions={{
                    icon: actionicon,
                    size: 'small',
                  }}
                  onClick={() => handleActionClick(action, requestItem.id)}
                />
              ),
            },
          ];
        }}
      />
    </ColumnFlexContainer>
  );
};
